'use client';

import classNames from 'classnames';
import CloseIcon from 'icons/lib/close.svg';
import FiltersIcon from 'icons/lib/filters.svg';
import Hamburger from 'icons/lib/menu.svg';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { setCookie } from 'utils';
import { formatCurrency, getCurrencyByLocale } from 'utils';

import { useTranslation } from '@/i18n/client';
import { fetchBrandsFilterList } from '@/modules/brands/api';
import { fetchFilterList } from '@/modules/product/api/productList';
import { FilterPillSkeleton } from '@/modules/product/components/productsList/filterPillSkeleton';
import { useProductList } from '@/modules/product/context/productList';
import {
  renderDiscountFilterLabels,
  renderFilterLabels,
  renderPriceFilterLabels,
} from '@/modules/product/pages/productsListPage/utils';
import { CATEGORY_THREE_OPEN_COOKIE_NAME } from '@/modules/product/utils';
import { routes } from '@/shared/data/routes';
import { OrderBy } from '@/shared/types/order-by';
import { useCurrentLocale } from '@/shared/utils/client';

import { FilterPill } from '../filterPill/FilterPill';
import { FilterSidebar } from '../filterSidebar';
import styles from './FilterBar.module.scss';
import {
  ActiveFilterList,
  CheckboxFilterExtended,
  ColorFilterExtended,
  DiscountFilterExtended,
  FilterBarProps,
  RangeFilterExtended,
} from './types';
import {
  formatActiveFilterList,
  formatSidebarFilterList,
  resetSidebarFilterList,
} from './utils';

export function FilterBar({ hasCategories }: FilterBarProps) {
  const { t } = useTranslation(['products', 'common']);
  const {
    products,
    categorySlug,
    filterList,
    filters,
    ascending,
    orderBy,
    brandSlug,
    filteredFilterList,
    isFiltersListLoaded,
    setProductMenuOpen,
    handleFetchFilters,
    setProductMenuMobileSidebarOpen,
  } = useProductList();
  const locale = useCurrentLocale();
  const pathname = usePathname();
  const [activeFilterView, setActiveFilterView] = useState<string | null>(null);
  const [isFiltersSidebarOpen, setIsFiltersSidebarOpen] = useState(false);
  const [sort, setSort] = useState(`${ascending}${orderBy}`);
  const [sidebarSort, setSidebarSort] = useState(`${ascending}${orderBy}`);
  const [activeFilterList, setActiveFilterList] = useState<ActiveFilterList>(
    () =>
      formatActiveFilterList(
        filterList,
        filters,
        formatCurrency(getCurrencyByLocale(locale))
      )
  );
  const [sidebarFilters, setSidebarFilters] = useState<ActiveFilterList>(() =>
    formatSidebarFilterList(
      filterList,
      filters,
      formatCurrency(getCurrencyByLocale(locale))
    )
  );
  const [sidebarFilteredFilterList, setSidebarFilteredFilterList] =
    useState(filteredFilterList);

  function renderPillChildren(
    filter:
      | RangeFilterExtended
      | CheckboxFilterExtended
      | ColorFilterExtended
      | DiscountFilterExtended
  ) {
    if (filter.selectedValue.length < 1) {
      return undefined;
    }

    switch (filter.filterType) {
      case 'color':
      case 'checkbox':
        return renderFilterLabels(filter.selectedValue);
      case 'range':
        return renderPriceFilterLabels(
          filter.selectedValue,
          formatCurrency(getCurrencyByLocale(locale))
        );
      case 'discount':
        return renderDiscountFilterLabels(filter.selectedValue, filter);
    }
  }

  function renderSortingLabel() {
    switch (sort) {
      case '1name':
        return t('productList.aToZ');
      case '0name':
        return t('productList.zToA');
      case '1price':
        return t('productList.leastExpensive');
      case '0price':
        return t('productList.mostExpensive');
      case '0sales_ratio':
        return t('productList.default');
      case '0updated_at':
        return t('productList.latest');
      case '1updated_at':
        return t('productList.oldest');

      default:
        return t('productList.aToZ');
    }
  }

  function handleResetFilters() {
    const newAscending = categorySlug === 'search' ? 1 : 0;
    const newOrderBy = categorySlug === 'search' ? 'name' : 'sales_ratio';
    const newFilters: Record<string, unknown> = {};

    setActiveFilterList([]);
    setSidebarFilters(() => resetSidebarFilterList(filteredFilterList));
    setSidebarFilteredFilterList(filterList);
    setSort(`${newAscending}${newOrderBy}`);
    setSidebarSort(`${newAscending}${newOrderBy}`);
    handleFetchFilters(newAscending, newOrderBy, newFilters);
  }

  async function handleResetFilter(slug: string) {
    const newSidebarFilterList = [...activeFilterList];
    const newFilters: Record<string, unknown> = {};

    const changedFilter = newSidebarFilterList.findIndex(
      (filter) => filter.slug === slug
    );

    newSidebarFilterList.splice(changedFilter, 1);

    newSidebarFilterList.forEach((filter) => {
      if (filter.selectedValue.length < 1) {
        return;
      }

      switch (filter.filterType) {
        case 'checkbox':
          newFilters[filter.slug] =
            filter.unit === null
              ? filter.selectedValue
              : filter.selectedValue.map((value) =>
                  value.replace(` ${filter.unit}`, '')
                );
          break;
        case 'color':
        case 'discount':
          newFilters[filter.slug] = filter.selectedValue;
          break;
        case 'range':
          newFilters[filter.slug] = {
            from: (
              Number(filter.selectedValue[0].split(' ')[0]) * 100
            ).toString(),
            to: (
              Number(filter.selectedValue[1].split(' ')[0]) * 100
            ).toString(),
          };
          break;
      }
    });

    setActiveFilterList(newSidebarFilterList);
    setSidebarFilters((oldValue) => {
      const newValue = [...oldValue];

      const sidebarFilterIndex = newValue.findIndex((f) => f.slug === slug);

      newValue[sidebarFilterIndex].selectedValue.length = 0;

      return newValue;
    });
    handleFetchFilters(
      Number(sort.charAt(0)),
      sort.substring(1) as OrderBy,
      newFilters
    );

    const response = brandSlug
      ? await fetchBrandsFilterList(brandSlug, newFilters)
      : await fetchFilterList(categorySlug, newFilters);

    setSidebarFilteredFilterList(response);
  }

  function openFiltersSidebar(filterView?: string | null) {
    setActiveFilterView(filterView || null);
    setIsFiltersSidebarOpen(true);
  }

  const closeFiltersSidebar = () => {
    setIsFiltersSidebarOpen(false);
  };

  const handleCategoryThreeSidebarClick = () => {
    setProductMenuOpen((prevValue) => {
      const newValue = !prevValue;
      setCookie(CATEGORY_THREE_OPEN_COOKIE_NAME, String(newValue));

      return newValue;
    });
  };

  const handleMobileCategoryThreeSidebarClick = () => {
    setProductMenuMobileSidebarOpen(true);
  };

  useEffect(() => {
    setActiveFilterList(() =>
      formatActiveFilterList(
        filterList,
        filters,
        formatCurrency(getCurrencyByLocale(locale))
      )
    );

    setSidebarFilters(() =>
      formatSidebarFilterList(
        filterList,
        filters,
        formatCurrency(getCurrencyByLocale(locale))
      )
    );
  }, [filterList, filters, locale]);

  useEffect(() => {
    setSidebarFilteredFilterList(filteredFilterList);
  }, [filteredFilterList]);

  return (
    <>
      <div className={styles.filtersBarWrapper}>
        <div className={styles['filtersBarWrapper']}>
          <div
            className={classNames(
              styles.filtersBarFiltersSelector,
              styles.filtersBarFiltersSelectorMobile
            )}
          >
            {isFiltersListLoaded ? (
              <>
                {categorySlug !== 'search' &&
                  categorySlug !== 'promotions' &&
                  categorySlug !== 'sellout' &&
                  !pathname?.includes(routes[locale].SINGLE_BRAND) && (
                    <FilterPill
                      onPress={handleMobileCategoryThreeSidebarClick}
                      label={t('productList.categories')}
                      icon={<Hamburger />}
                    />
                  )}
                {categorySlug !== 'search' &&
                  filterList.length > 0 &&
                  products.length > 0 && (
                    <FilterPill
                      icon={<FiltersIcon />}
                      onPress={() => openFiltersSidebar()}
                      label={
                        activeFilterList.length > 0
                          ? ''
                          : t('productList.allFilters')
                      }
                    >
                      {activeFilterList.length > 0
                        ? `${t('productList.allFilters')} (${activeFilterList.length})`
                        : undefined}
                    </FilterPill>
                  )}
              </>
            ) : (
              <>
                <FilterPillSkeleton />
                <FilterPillSkeleton />
              </>
            )}
          </div>
          <div
            className={classNames(
              styles.filtersBarFiltersSelector,
              styles.filtersBarFiltersSelectorDesktop
            )}
          >
            {isFiltersListLoaded ? (
              <>
                {categorySlug !== 'search' &&
                  categorySlug !== 'promotions' &&
                  categorySlug !== 'sellout' &&
                  hasCategories &&
                  !pathname?.includes(routes[locale].SINGLE_BRAND) && (
                    <FilterPill
                      onPress={handleCategoryThreeSidebarClick}
                      label={t('productList.categories')}
                      icon={<Hamburger />}
                    />
                  )}

                {categorySlug !== 'search' &&
                  filterList.map((filter, index) => {
                    if (filter.slug !== 'price' && filter.slug !== 'brand') {
                      return null;
                    }

                    const selectedFilter = activeFilterList.find(
                      (f) => f.slug === filter.slug
                    );

                    return (
                      <FilterPill
                        key={index}
                        label={filter.name}
                        onPress={() => openFiltersSidebar(filter.slug)}
                        onClear={() => handleResetFilter(filter.slug)}
                      >
                        {renderPillChildren(
                          selectedFilter ?? { ...filter, selectedValue: [] }
                        )}
                      </FilterPill>
                    );
                  })}

                {categorySlug !== 'search' &&
                  activeFilterList.map((filter, index) => {
                    if (filter.slug === 'price' || filter.slug === 'brand') {
                      return null;
                    }

                    return (
                      <FilterPill
                        key={index}
                        label={filter.name}
                        onPress={() => openFiltersSidebar(filter.slug)}
                        onClear={() => handleResetFilter(filter.slug)}
                      >
                        {renderPillChildren(filter)}
                      </FilterPill>
                    );
                  })}

                {categorySlug !== 'search' && filterList.length > 0 && (
                  <FilterPill
                    icon={<FiltersIcon />}
                    onPress={() => openFiltersSidebar()}
                    label={
                      activeFilterList.length > 0
                        ? ''
                        : t('productList.allFilters')
                    }
                  >
                    {activeFilterList.length > 0
                      ? `${t('productList.allFilters')} (${activeFilterList.length})`
                      : undefined}
                  </FilterPill>
                )}

                {categorySlug !== 'search' && activeFilterList.length > 0 && (
                  <FilterPill
                    label={t('productList.clearFiltersLong')}
                    onPress={handleResetFilters}
                    icon={<CloseIcon />}
                    type="error"
                  />
                )}

                {products.length > 0 ? (
                  <>
                    <div className={styles.space}></div>
                    <FilterPill
                      isBold={
                        categorySlug === 'search'
                          ? sort !== '1name'
                          : sort !== '0sales_ratio'
                      }
                      onPress={() => openFiltersSidebar('sorter')}
                      label={t('productList.sorting')}
                    >
                      {renderSortingLabel()}
                    </FilterPill>
                  </>
                ) : null}
              </>
            ) : (
              <>
                <FilterPillSkeleton />
                <FilterPillSkeleton />
                {!brandSlug && (
                  <>
                    <FilterPillSkeleton />
                    <FilterPillSkeleton />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <FilterSidebar
        isOpen={isFiltersSidebarOpen}
        selectedFilter={activeFilterView}
        activeFilterList={activeFilterList}
        sidebarFilteredFilterList={sidebarFilteredFilterList}
        sidebarSort={sidebarSort}
        sort={sort}
        sidebarFilters={sidebarFilters}
        setActiveFilterList={setActiveFilterList}
        setSidebarSort={setSidebarSort}
        setSidebarFilters={setSidebarFilters}
        setSidebarFilteredFilterList={setSidebarFilteredFilterList}
        setSort={setSort}
        onClose={closeFiltersSidebar}
      />
    </>
  );
}
